import React from "react";

import { Box, Divider, Grid } from "@mui/material";
import { EmptyState } from "../../../components";
import { Search } from "@tiny/icons";
import TemplateSection from "../TemplateSection";
import { DragAndDropTabProps } from "./DragAndDropTab.types";
import { useDragAndDropTab } from "./DragAndDropTab.hooks";
import { DragAndDropTabProvider } from "./DragAndDropTab.context";
import DragAndDropHeader from "./DragAndDropHeader";
import FilteredSection from "./FilteredSection";
import ComponentLoader from "../../ComponentLoader/ComponentLoader";
import useBreakpoints from "../../../hooks/useBreakpoints";

function DragAndDropTab(props: DragAndDropTabProps) {
  const dragAndDropView = useDragAndDropTab(props);

  const { mdUp } = useBreakpoints();

  const {
    colorfulTemplates,
    professionalTemplates,
    minimalistTemplates,
    refetchSections,
    savedTemplates,
    showSelected,
    staffPicksTemplates,
    shouldShowSavedAndStaffPicks,
    shouldShowSaved,
    shouldShowColorful,
    shouldShowProfessional,
    shouldShowMinimalist,
    totalSaved,
    totalStaffPicks,
    totalColorful,
    totalProfessional,
    totalMinimalist,
    isEmpty,
    emptyStateMessage,
    isLastSaved,
    isLastStaffPicks,
    isLastColorful,
    isLastProfessional,
    isLastMinimalist,
    isLoading,
    isLoadingColorful,
    isLoadingProfessional,
    isLoadingMinimalist,
    isLoadingSaved,
    isLoadingStaffPicks,
    shouldShowFilteredSection,
    activeTitle,
    filteredTemplates,
    activeSaved,
    activeType,
    fetchMore,
    hasMore,
    totalFilteredTemplates,
    activeRenderWithEjs,
    showAll,
    isSelected,
    toggleSelect,
    selectedTemplate,
    selectable,
  } = dragAndDropView;

  const renderContent = () => {
    if (isEmpty) {
      return (
        <Box bgcolor="grey.50" pt={10} pb={10}>
          <EmptyState
            title="No templates found"
            message={emptyStateMessage}
            icon={<Search />}
            iconColor="primary"
            circleColor="white"
            align="center"
          />
        </Box>
      );
    }
    if (shouldShowFilteredSection) {
      return (
        <FilteredSection
          title={activeTitle}
          templates={filteredTemplates}
          saved={activeSaved}
          type={activeType}
          fetchMore={fetchMore}
          hasMore={hasMore}
          total={totalFilteredTemplates}
          renderWithEjs={activeRenderWithEjs}
          isSelected={isSelected}
          toggleSelect={toggleSelect}
        />
      );
    } else {
      return (
        <>
          {(isLoading || showSelected) && (
            <>
              <SectionDivider />
              <Box px={mdUp ? 6 : 0} pt={mdUp ? 1 : 0} pb={mdUp ? 6 : 0}>
                <TemplateSection
                  title="Currently selected"
                  showAll={showAll}
                  total={0}
                  favoritable={false}
                  hasMore={false}
                  templates={[selectedTemplate!]}
                  refetch={
                    !!refetchSections?.find((s) => s === "Currently selected")
                  }
                  selectable={false}
                  isSelected={isSelected}
                  toggleSelect={toggleSelect}
                  isCurrentSelected
                  loading={isLoading}
                />
              </Box>
            </>
          )}
          {(isLoadingSaved ||
            isLoadingStaffPicks ||
            shouldShowSavedAndStaffPicks) && (
            <>
              <SectionDivider />
              <Box p={mdUp ? 6 : 0}>
                <Grid container spacing={mdUp ? 4 : 0}>
                  {isLoadingSaved || shouldShowSaved ? (
                    <>
                      <Grid item xs={12} md={6}>
                        <TemplateSection
                          title="Saved"
                          showAll={showAll}
                          total={totalSaved}
                          hasMore={!isLastSaved}
                          saved
                          templates={savedTemplates}
                          columnSize={4}
                          refetch={
                            !!refetchSections?.find((s) => s === "Saved")
                          }
                          selectable={selectable}
                          isSelected={isSelected}
                          toggleSelect={toggleSelect}
                          loading={isLoadingSaved}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        {!mdUp && <SectionDivider />}
                        <TemplateSection
                          title={"Staff picks"}
                          showAll={showAll}
                          total={totalStaffPicks}
                          hasMore={!isLastStaffPicks}
                          templates={staffPicksTemplates}
                          columnSize={4}
                          type="STAFF_PICKED"
                          refetch={
                            !!refetchSections?.find((s) => s === "Staff picks")
                          }
                          isSelected={isSelected}
                          toggleSelect={toggleSelect}
                          selectable={selectable}
                          selectDisabled
                          loading={isLoadingStaffPicks}
                        />
                      </Grid>
                    </>
                  ) : (
                    <Grid item xs={12} md={12}>
                      <TemplateSection
                        title={"Staff picks"}
                        showAll={showAll}
                        total={totalStaffPicks}
                        hasMore={!isLastStaffPicks}
                        templates={staffPicksTemplates}
                        columnSize={2}
                        type="STAFF_PICKED"
                        refetch={
                          !!refetchSections?.find((s) => s === "Staff picks")
                        }
                        isSelected={isSelected}
                        toggleSelect={toggleSelect}
                        selectable={selectable}
                        selectDisabled
                        loading={isLoadingStaffPicks}
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>
            </>
          )}
          {(isLoadingColorful || shouldShowColorful) && (
            <>
              <SectionDivider />
              <Box p={mdUp ? 6 : 0}>
                <TemplateSection
                  title="Colorful"
                  description="Give your content a bold, vibrant look that grabs attention"
                  showAll={showAll}
                  total={totalColorful}
                  hasMore={!isLastColorful}
                  templates={colorfulTemplates}
                  type="COLORFUL"
                  refetch={!!refetchSections?.find((s) => s === "Colorful")}
                  isSelected={isSelected}
                  toggleSelect={toggleSelect}
                  selectable={selectable}
                  selectDisabled
                  loading={isLoadingColorful}
                />
              </Box>
            </>
          )}
          {(isLoadingProfessional || shouldShowProfessional) && (
            <>
              <SectionDivider />
              <Box p={mdUp ? 6 : 0}>
                <TemplateSection
                  title="Professional"
                  description="Make your emails feel polished and business-ready from the first impression"
                  showAll={showAll}
                  total={totalProfessional}
                  hasMore={!isLastProfessional}
                  templates={professionalTemplates}
                  type="PROFESSIONAL"
                  refetch={!!refetchSections?.find((s) => s === "Professional")}
                  isSelected={isSelected}
                  toggleSelect={toggleSelect}
                  selectable={selectable}
                  selectDisabled
                  loading={isLoadingProfessional}
                />
              </Box>
            </>
          )}
          {(isLoadingMinimalist || shouldShowMinimalist) && (
            <>
              <SectionDivider />
              <Box p={mdUp ? 6 : 0}>
                <TemplateSection
                  title="Minimalist"
                  description="Let your content shine without the noise — less is more"
                  showAll={showAll}
                  total={totalMinimalist}
                  hasMore={!isLastMinimalist}
                  templates={minimalistTemplates}
                  type="MINIMALIST"
                  refetch={!!refetchSections?.find((s) => s === "Minimalist")}
                  isSelected={isSelected}
                  toggleSelect={toggleSelect}
                  selectable={selectable}
                  selectDisabled
                  loading={isLoadingMinimalist}
                />
              </Box>
            </>
          )}
        </>
      );
    }
  };

  return (
    <DragAndDropTabProvider value={dragAndDropView}>
      <Box>
        <DragAndDropHeader />
        {isLoading ? <ComponentLoader height="400px" /> : renderContent()}
      </Box>
    </DragAndDropTabProvider>
  );
}

function SectionDivider() {
  const { mdUp } = useBreakpoints();
  return (
    <Box ml={mdUp ? 6 : 0} mr={mdUp ? 6 : 0}>
      <Divider />
    </Box>
  );
}

export default DragAndDropTab;
